<template>
  <component :is="component">
    <!-- Logo -->
    <template v-if="showImage">
      <img
        :src="logoSrc"
        alt
        :title="brandName"
        class="u-brand-image"
        :class="{
          'as-logomark': !!logomark
        }"
      />
    </template>
    <!-- Icon -->
    <template v-else-if="showIcon">
      <span class="u-brand-icon">
        <b-icon v-if="!brandName.length" icon="asterisk" />
        <strong v-else>{{ initials }}</strong>
      </span>
    </template>
    <!-- Text -->
    <template v-else>
      <span class="u-brand-text">{{ brandName }}</span>
    </template>
  </component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import store from "@/store";
import _ from "@/boot/lodash";
import { UUID } from "@/data/enums";
import type { PropType, CreateElement, RenderContext, VNodeData } from "vue";
import type { IBrand } from "@/models/brands";
import type { Route } from "vue-router";

enum FallbackOptions {
  ICON = "icon",
  TEXT = "text"
}

export default defineComponent({
  name: "BrandLogo",
  props: {
    brand: {
      type: Object as PropType<IBrand | undefined>,
      default: () =>
        store.state.brand?.id === UUID.ORG ? undefined : store.state.brand
    },
    logomark: { type: Boolean, default: false },
    size: { type: String, default: "3rem" },
    quality: {
      type: Number as PropType<50 | 100 | 200 | 300 | 400>,
      default: 200
    },
    tag: {
      type: String as PropType<"div" | "span" | "router-link">,
      default: "router-link"
    },
    to: { type: Object as PropType<Route>, default: () => ({ path: "/" }) },
    fallback: {
      type: String as PropType<FallbackOptions>,
      validator: (val: any) => _.values(FallbackOptions).includes(val),
      default: FallbackOptions.ICON
    }
  },
  computed: {
    component() {
      const vNodeData = {
        style: { fontSize: this.size },
        class: ["u-brand-logo", this.showText && "has-text-fallback"]
      } as VNodeData;
      return {
        functional: true,
        render: (h: CreateElement, { children }: RenderContext) =>
          this.tag === "router-link"
            ? h(this.tag, { ...vNodeData, props: { to: this.to } }, children)
            : h(this.tag, vNodeData, children)
      };
    },
    brandName() {
      return this.brand?.name || "";
    },
    logoSrc() {
      // If requested, try and return logomark (icon) url
      if (this.logomark && this.brand?.icon?.full_url)
        return this.brand?.icon?.full_url;
      // Otherwise, return default image url
      try {
        const src = new URL(this.brand?.image?.full_url as string);
        const q = this.quality;
        if (q) src.searchParams.set("size", `${q}x${q}`);
        return src.toString();
      } catch {
        return "";
      }
    },
    showImage() {
      return !!this.logoSrc;
    },
    showIcon() {
      return this.fallback === FallbackOptions.ICON;
    },
    showText() {
      return !this.showImage && !this.showIcon;
    },
    initials() {
      const name = this.brandName
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .trim()
        .split(" ");
      if (name.length > 1) {
        const first = this.$_.first(this.$_.first(name)) || "";
        const last = this.$_.first(this.$_.last(name)) || "";
        return `${first + last}`.toUpperCase();
      }
      return name[0].substring(0, 2).toUpperCase();
    }
  }
});
</script>

<style lang="scss" scoped>
.u-brand-logo {
  display: flex;
  align-items: center;
  font-size: 3rem; // Can be overridden in template
  min-width: 1em;
  height: 1em;

  // Image
  img.u-brand-image {
    display: block;
    object-fit: contain;
    height: 1em;
    max-width: 3em; // Max aspect ratio of 3:1
    &.as-logomark {
      width: 1em;
    }
  }

  // Icon
  .u-brand-icon {
    width: 1em;
    height: 1em;
    display: flex;
    justify-content: center;
    align-items: center;
    background: $black;
    color: $black-invert;
    border-radius: $radius-large;
    > span.icon {
      font-size: 1rem;
    }
    > strong {
      font-size: 0.5em;
    }
  }

  // Text
  &.has-text-fallback {
    padding: 0 0.25em;
    background: $light;
    border-radius: 0.125em;
    box-shadow: inset 0 0 0 1px transparent;
    transition: box-shadow 0.12s ease-out;
    &:hover {
      box-shadow: inset 0 0 0 1px var(--brand-color);
    }
    .u-brand-text {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-family: $family-secondary;
      font-weight: $weight-semibold;
      font-size: 0.5em; // Relative to logo, default would be 24px
      color: $dark;
    }
  }
}
</style>
